import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  promotionsApi,
  type CreateCouponInput,
  type PromotionsFilter,
  type UpdateCouponInput,
} from './promotions-api';

export const KEY_PROMOTIONS = ['promotions'];

export function usePromotionsList(filter: PromotionsFilter = {}, enabled = true) {
  return useQuery({
    queryKey: [...KEY_PROMOTIONS, 'list', filter],
    queryFn: () => promotionsApi.list(filter),
    enabled,
    placeholderData: (prev) => prev,
  });
}

export function usePromotionKpis() {
  return useQuery({
    queryKey: [...KEY_PROMOTIONS, 'kpis'],
    queryFn: () => promotionsApi.getKpis(),
    refetchInterval: 30_000,
  });
}

export function usePromotion(id: string | undefined) {
  return useQuery({
    queryKey: [...KEY_PROMOTIONS, 'detail', id],
    queryFn: () => promotionsApi.get(id!),
    enabled: !!id,
  });
}

export function useCreatePromotion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCouponInput) => promotionsApi.create(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY_PROMOTIONS });
    },
  });
}

export function useUpdatePromotion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCouponInput }) =>
      promotionsApi.update(id, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY_PROMOTIONS });
    },
  });
}

export function useTogglePromotionStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      promotionsApi.toggleStatus(id, isActive),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY_PROMOTIONS });
    },
  });
}
